import { useState } from 'react';
import type { Brief } from '@/entities/brief';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useStartBriefFlow } from '@/shared/hooks';
import { StartBriefPopover } from '@/shared/ui';
import type { BriefArtifactRef, BriefSubmitPayload } from '../../model/brief-generation.types';
import { BriefGenerationProgressModal } from '../BriefGenerationProgressModal';
import { BriefIntakeModal } from '../BriefIntakeModal';

export interface BriefStartFlowProps {
  /** Trigger element the project picker is anchored to (e.g. an icon button). */
  children?: React.ReactNode;
  /**
   * Override what happens once the brief is created. Defaults to opening the
   * brief detail in a new tab (handled by `useStartBriefFlow`). Consumers that
   * need extra behavior — e.g. the strategic-territories widget kicking off an
   * agent review — pass it here. Receives the chosen project id + the brief.
   */
  onBriefCreated?: (projectId: string, brief: Brief) => void;
  /**
   * Resolves artifacts to open the intake with already attached, for entry points
   * that start a brief *from* something. Runs once when the picker opens, and the
   * picker stays in its loading state until it settles, so the user cannot reach
   * intake before the result is in. A rejection is swallowed: the brief is still
   * worth starting without the attachment.
   */
  prepareArtifacts?: () => Promise<BriefArtifactRef[]>;
}

/**
 * Self-contained "Start Brief" entry point: a project-picker popover that runs
 * the intake → progress flow for the chosen project and opens the created brief.
 *
 * This is the composition home for the flow. It owns the orchestration so
 * consumers (the config-layer widget bindings, pages) only render
 * `<BriefStartFlow>` around a trigger — they never wire the modals or thread
 * the payload themselves.
 *
 * The picker + headless state machine live in `shared` (`StartBriefPopover`,
 * `useStartBriefFlow`) so they stay feature-agnostic; this component supplies
 * the brief-generation-specific modals.
 */
export function BriefStartFlow({
  children,
  onBriefCreated,
  prepareArtifacts,
}: BriefStartFlowProps) {
  const workspaceId = useCurrentWorkspaceId();
  const flow = useStartBriefFlow<BriefSubmitPayload>({ onBriefCreated });
  const [preparing, setPreparing] = useState(false);
  const [preparedArtifacts, setPreparedArtifacts] = useState<BriefArtifactRef[]>([]);

  // Prepare on open rather than on mount: an entry point that mints something
  // should only do so once the user shows intent, not for everyone who loads
  // the surface.
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen || !prepareArtifacts || preparing || preparedArtifacts.length > 0) return;

    setPreparing(true);
    prepareArtifacts()
      .then(setPreparedArtifacts)
      .catch(() => setPreparedArtifacts([]))
      .finally(() => setPreparing(false));
  };

  return (
    <>
      <StartBriefPopover
        workspaceId={workspaceId}
        onProjectSelected={flow.selectProject}
        onOpenChange={handleOpenChange}
        additionalLoadingCheck={preparing}
      >
        {children}
      </StartBriefPopover>

      {flow.platformProjectId && flow.intakeOpen && (
        <BriefIntakeModal
          open
          onClose={flow.reset}
          onGenerate={flow.handleGenerate}
          initialArtifacts={preparedArtifacts}
        />
      )}

      {flow.platformProjectId && !flow.intakeOpen && flow.payload && (
        <BriefGenerationProgressModal
          open
          onClose={flow.reset}
          projectId={flow.platformProjectId}
          payload={flow.payload}
          onSuccess={flow.handleSuccess}
        />
      )}
    </>
  );
}
